import React, { useState, useEffect } from "react";
import {
  AppBar, Box, Toolbar, Typography, Button, IconButton, Drawer,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Avatar
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import GridOnIcon from "@mui/icons-material/GridOn";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import useMediaQuery from "@mui/material/useMediaQuery";

// Bottom tab items (mobile)
const tabs = [
  { label: "Galeria",   to: "/gallery",   icon: <GridOnIcon />,          activeIcon: <GridOnIcon sx={{ color: "#EB578A" }} /> },
  { label: "Dodaj",     to: "/upload",    icon: <AddBoxOutlinedIcon />,   activeIcon: <AddBoxOutlinedIcon sx={{ color: "#EB578A" }} /> },
  { label: "Ulubione",  to: "/favorites", icon: <FavoriteBorderIcon />,   activeIcon: <FavoriteIcon sx={{ color: "#EB578A" }} /> },
  { label: "Profil",    to: "/profile",   icon: <PersonOutlineIcon />,    activeIcon: <AccountCircleIcon sx={{ color: "#EB578A" }} /> },
];

// Desktop nav links
const navLinks = [
  { label: "Galeria",   to: "/gallery",   icon: <PhotoLibraryIcon /> },
  { label: "Dodaj",     to: "/upload",    icon: <AddCircleOutlineIcon /> },
  { label: "Ulubione",  to: "/favorites", icon: <FavoriteIcon /> },
  { label: "Profil",    to: "/profile",   icon: <AccountCircleIcon /> },
];

const Navbar = () => {
  const { isAuthenticated, logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:800px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");
  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase();

  const handleLogout = () => {
    logout();
    navigate("/login");
    setDrawerOpen(false);
  };

  // Shared-favorites: minimal bar only
  if (location.pathname.startsWith("/shared-favorites")) {
    return (
      <AppBar position="fixed" sx={{
        background: "linear-gradient(90deg, #EB578A 0%, #C0145E 100%)",
        boxShadow: "0 2px 12px rgba(235,87,138,0.25)",
        pt: "env(safe-area-inset-top)",
      }}>
        <Toolbar sx={{ minHeight: 56, justifyContent: "center" }}>
          <FavoriteIcon sx={{ color: "rgba(255,255,255,0.85)", mr: 1, fontSize: 20 }} />
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 800, letterSpacing: 0.5 }}>
            Księga Gości
          </Typography>
        </Toolbar>
      </AppBar>
    );
  }

  // ── Desktop drawer (hamburger on tablet/desktop when needed) ──────────────
  const drawer = (
    <Box sx={{ width: 280, bgcolor: "#fff", height: "100%" }} role="presentation" onClick={() => setDrawerOpen(false)}>
      <Box sx={{ background: "linear-gradient(135deg, #EB578A 0%, #C0145E 100%)", p: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
        <FavoriteIcon sx={{ color: "rgba(255,255,255,0.9)", fontSize: 26 }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>Księga Gości</Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>kalmarry.pl</Typography>
        </Box>
      </Box>

      {isAuthenticated && user && (
        <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: "#EB578A", color: "#fff", fontWeight: 700 }}>{initials}</Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>{user.first_name} {user.last_name}</Typography>
            <Typography sx={{ fontSize: 12, color: "#aaa" }}>Gość</Typography>
          </Box>
        </Box>
      )}

      <Divider sx={{ borderColor: "#f0e6eb" }} />
      <List sx={{ pt: 1 }}>
        {isAuthenticated ? (
          <>
            {navLinks.map(link => (
              <ListItem key={link.label} disablePadding>
                <ListItemButton component={Link} to={link.to} selected={isActive(link.to)}
                  sx={{ py: 1.2, px: 2.5, borderRadius: 2, mx: 1, color: isActive(link.to) ? "#EB578A" : "#444", bgcolor: isActive(link.to) ? "#fff0f5" : "transparent", "&:hover": { bgcolor: "#fff0f5", color: "#EB578A" }, "&.Mui-selected": { bgcolor: "#fff0f5", color: "#EB578A" } }}>
                  <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>{link.icon}</ListItemIcon>
                  <ListItemText primary={link.label} primaryTypographyProps={{ fontWeight: isActive(link.to) ? 700 : 500, fontSize: 15 }} />
                </ListItemButton>
              </ListItem>
            ))}
            <Divider sx={{ my: 1, borderColor: "#f0e6eb" }} />
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout} sx={{ py: 1.2, px: 2.5, borderRadius: 2, mx: 1, color: "#e53935", "&:hover": { bgcolor: "#fff0f0" } }}>
                <ListItemIcon sx={{ minWidth: 36, color: "#e53935" }}><LogoutIcon /></ListItemIcon>
                <ListItemText primary="Wyloguj" primaryTypographyProps={{ fontWeight: 600, fontSize: 15 }} />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/login" sx={{ py: 1.2, px: 2.5, borderRadius: 2, mx: 1, color: "#444", "&:hover": { bgcolor: "#fff0f5", color: "#EB578A" } }}>
                <ListItemIcon sx={{ minWidth: 36, color: "#EB578A" }}><LoginIcon /></ListItemIcon>
                <ListItemText primary="Logowanie" primaryTypographyProps={{ fontWeight: 500, fontSize: 15 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/register" sx={{ py: 1.2, px: 2.5, borderRadius: 2, mx: 1, color: "#444", "&:hover": { bgcolor: "#fff0f5", color: "#EB578A" } }}>
                <ListItemIcon sx={{ minWidth: 36, color: "#EB578A" }}><PersonAddIcon /></ListItemIcon>
                <ListItemText primary="Rejestracja" primaryTypographyProps={{ fontWeight: 500, fontSize: 15 }} />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  // ── MOBILE layout: slim top bar + bottom tab bar ─────────────────────────
  if (isMobile) {
    return (
      <>
        {/* Slim top bar — brand only */}
        <AppBar position="fixed" elevation={0} sx={{
          background: "#fff",
          borderBottom: "1px solid #f0e8ed",
          boxShadow: scrolled ? "0 2px 12px rgba(235,87,138,0.1)" : "none",
          transition: "box-shadow 0.3s",
          pt: "env(safe-area-inset-top)",
          zIndex: 1200,
        }}>
          <Toolbar sx={{ minHeight: 52, px: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography
              component={Link}
              to={isAuthenticated ? "/gallery" : "/login"}
              sx={{
                fontWeight: 900, fontSize: 22, letterSpacing: "-0.5px",
                textDecoration: "none",
                background: "linear-gradient(90deg, #EB578A, #C0145E)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontFamily: "'Lato', Arial, sans-serif",
              }}
            >
              Księga Gości
            </Typography>

            {isAuthenticated ? (
              <Avatar
                component={Link}
                to="/profile"
                sx={{ width: 32, height: 32, bgcolor: "#EB578A", color: "#fff", fontWeight: 700, fontSize: 13, textDecoration: "none", cursor: "pointer" }}
              >
                {initials}
              </Avatar>
            ) : (
              <IconButton component={Link} to="/login" sx={{ color: "#EB578A" }}>
                <LoginIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>

        {/* Bottom tab bar */}
        {isAuthenticated && (
          <Box sx={{
            position: "fixed",
            bottom: 0, left: 0, right: 0,
            zIndex: 1200,
            bgcolor: "#fff",
            borderTop: "1px solid #f0e8ed",
            boxShadow: "0 -4px 20px rgba(235,87,138,0.08)",
            display: "flex",
            pb: "env(safe-area-inset-bottom)",
          }}>
            {tabs.map(tab => {
              const active = isActive(tab.to);
              return (
                <Box
                  key={tab.to}
                  component={Link}
                  to={tab.to}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 1,
                    gap: 0.3,
                    textDecoration: "none",
                    color: active ? "#EB578A" : "#bbb",
                    transition: "color 0.15s",
                    "& svg": { fontSize: 24 },
                    "&:active": { opacity: 0.7 },
                  }}
                >
                  {active ? tab.activeIcon : tab.icon}
                  <Typography sx={{ fontSize: 10, fontWeight: active ? 700 : 400, color: "inherit", lineHeight: 1 }}>
                    {tab.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </>
    );
  }

  // ── DESKTOP layout ────────────────────────────────────────────────────────
  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="fixed" sx={{
        background: "linear-gradient(90deg, #EB578A 0%, #C0145E 100%)",
        boxShadow: scrolled ? "0 4px 20px rgba(235,87,138,0.3)" : "0 2px 8px rgba(235,87,138,0.15)",
        transition: "box-shadow 0.3s",
        zIndex: theme => theme.zIndex.drawer + 1,
        pt: "env(safe-area-inset-top)",
      }}>
        <Toolbar sx={{ minHeight: 64, gap: 0.5 }}>
          <Typography
            variant="h6" component={Link}
            to={isAuthenticated ? "/gallery" : "/login"}
            sx={{ color: "#fff", textDecoration: "none", fontWeight: 800, fontSize: 20, letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 0.8, mr: 3 }}
          >
            <FavoriteIcon sx={{ fontSize: 22, opacity: 0.9 }} />
            Księga Gości
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          {isAuthenticated ? (
            <>
              {navLinks.map(link => (
                <Button key={link.label} color="inherit" component={Link} to={link.to} startIcon={link.icon}
                  sx={{ ml: 0.5, fontWeight: isActive(link.to) ? 700 : 400, fontSize: 14, px: 1.5, py: 0.6, borderRadius: 2, bgcolor: isActive(link.to) ? "rgba(255,255,255,0.18)" : "transparent", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" }, textTransform: "none" }}>
                  {link.label}
                </Button>
              ))}
              <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}
                sx={{ ml: 0.5, fontWeight: 400, fontSize: 14, px: 1.5, py: 0.6, borderRadius: 2, textTransform: "none", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>
                Wyloguj
              </Button>
              <Box sx={{ display: "flex", alignItems: "center", ml: 2, gap: 1 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: "rgba(255,255,255,0.25)", color: "#fff", fontWeight: 700, fontSize: 14, border: "1.5px solid rgba(255,255,255,0.5)" }}>
                  {initials}
                </Avatar>
                <Typography sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 600, fontSize: 14 }}>
                  {user?.first_name}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Button color="inherit" component={Link} to="/login" startIcon={<LoginIcon />} sx={{ textTransform: "none", fontWeight: 500, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>Logowanie</Button>
              <Button color="inherit" component={Link} to="/register" startIcon={<PersonAddIcon />} sx={{ textTransform: "none", fontWeight: 500, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>Rejestracja</Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        sx={{ "& .MuiDrawer-paper": { width: 280, borderTopRightRadius: 20, borderBottomRightRadius: 20, border: "none", boxShadow: "4px 0 24px rgba(235,87,138,0.12)" } }}>
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Navbar;
